/**
 * حسابُ مستطيل القصّ — <b>نقيٌّ ومفصولٌ عمدًا</b>.
 *
 * هذا هو الموضعُ الذي يُخطئ صامتًا: مستطيلٌ يخرج عن الصورة فيُقصّ سوادٌ، أو
 * ينقلب عرضُه سالبًا عند السحب فوق الحافّة المقابلة فيُرمى <c>drawImage</c>
 * بأبعادٍ لا معنى لها. ولا يظهر شيءٌ من ذلك في بناءٍ ولا في نوع — يظهر في صورةٍ
 * حُفظت خطأً بعد أن رآها الموظّفُ صحيحةً على الشاشة.
 *
 * ومفصولٌ عن المكوّن ليُختبَر بلا متصفّحٍ ولا <c>canvas</c>.
 */

export type Rect = { x: number; y: number; w: number; h: number }

export type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se'

/** أصغرُ ضلعٍ يمكن الإمساكُ به بإصبع. */
export const MIN_SIDE = 32

export function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), Math.max(low, high))
}

/**
 * حدودُ المستطيل بعد سحبٍ مقدارُه <c>(dx, dy)</c>.
 *
 * <b>ولا يخرج عن الصورة بحال</b>: السحبُ يتوقّف عند الحافّة ولا يعبرها. وركنٌ
 * يُسحب فوق الركن المقابل يتوقّف عند <see cref="MIN_SIDE"/> ولا ينقلب — فالعرضُ
 * والارتفاعُ موجبان دائمًا.
 */
export function applyDrag(
  handle: Handle,
  from: Rect,
  dx: number,
  dy: number,
  maxW: number,
  maxH: number,
): Rect {
  if (handle === 'move') {
    // النقلُ لا يغيّر الحجم: يُقيَّد الموضعُ وحدَه بما يُبقي المستطيلَ داخلًا.
    return {
      ...from,
      x: clamp(from.x + dx, 0, maxW - from.w),
      y: clamp(from.y + dy, 0, maxH - from.h),
    }
  }

  const west = handle === 'nw' || handle === 'sw'
  const north = handle === 'nw' || handle === 'ne'

  let { x, y, w, h } = from

  if (west) {
    // الحافّةُ اليسرى تتحرّك والحافّةُ اليمنى ثابتة، فيُحسب العرضُ من الفرق.
    const next = clamp(from.x + dx, 0, from.x + from.w - MIN_SIDE)
    w = from.x + from.w - next
    x = next
  } else {
    w = clamp(from.w + dx, MIN_SIDE, maxW - from.x)
  }

  if (north) {
    const next = clamp(from.y + dy, 0, from.y + from.h - MIN_SIDE)
    h = from.y + from.h - next
    y = next
  } else {
    h = clamp(from.h + dy, MIN_SIDE, maxH - from.y)
  }

  return { x, y, w, h }
}

/**
 * أبعادُ العرض داخل صندوقٍ مع حفظ النسبة — <b>ولا تكبير</b>.
 *
 * والنسبةُ المُعادة (<c>scale</c>) هي ما يُقسَم عليه لتعود إحداثيّاتُ العرض
 * بكسلاتِ المصدر. ولولا إعادتُها لَحُفظت صورةٌ بحجم المعاينة لا بحجم الأصل.
 */
export function fitInside(w: number, h: number, maxW: number, maxH: number) {
  if (w <= 0 || h <= 0 || maxW <= 0 || maxH <= 0) {
    return { w: 0, h: 0, scale: 1 }
  }

  const scale = Math.min(maxW / w, maxH / h, 1)

  return { w: Math.round(w * scale), h: Math.round(h * scale), scale }
}

/**
 * أبعادُ الناتج: بكسلاتُ المصدر، مُصغَّرةً إن تجاوزت الحدَّ الأطول.
 */
export function outputSize(regionW: number, regionH: number, scale: number, maxEdge: number) {
  const fullW = Math.max(1, Math.round(regionW / scale))
  const fullH = Math.max(1, Math.round(regionH / scale))
  const shrink = Math.min(1, maxEdge / Math.max(fullW, fullH))

  return {
    fullW,
    fullH,
    outW: Math.max(1, Math.round(fullW * shrink)),
    outH: Math.max(1, Math.round(fullH * shrink)),
  }
}
